import { useCallback, useEffect, useState } from 'react'
import { ApiClientError, type Contact, type Page, type PageRequest } from '../api/types.js'
import { IconContacts } from '../icons.js'
import { Alert } from '../ui/Alert.js'

/**
 * Узкий срез клиента: экрану нужны только контакты инстанса и переименование
 * (барьер 2 — клиент API есть граница процесса для экранов админки, его
 * двойник в тестах разрешён, см. Instances.tsx).
 */
export interface ContactsClient {
  contacts(instanceId: string, page?: PageRequest): Promise<Page<Contact>>
  renameContact(id: string, name: string | null): Promise<Contact>
}

export interface ContactsProps {
  client: ContactsClient
  instanceId: string
  /** Права, выданные при входе (session.permissions()) — экран сам их не запрашивает. */
  permissions: string[]
}

const CAN_WRITE = 'contacts.write'

/** Сообщение по умолчанию для отказов не от сервера (например, обрыв сети). */
const GENERIC_FAILURE = 'Не удалось выполнить действие. Проверьте соединение и попробуйте ещё раз.'

export function Contacts(props: ContactsProps) {
  const { client, instanceId, permissions } = props
  const canWrite = permissions.includes(CAN_WRITE)

  const [contacts, setContacts] = useState<Contact[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Строка с открытой формой переименования — только одна за раз, как
  // и множество busy у Instances.tsx: повторное нажатие на ту же строку,
  // пока запрос летит, не должно отправлять его дважды.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftName, setDraftName] = useState('')
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set())

  const load = useCallback(async () => {
    try {
      const page = await client.contacts(instanceId)
      setContacts(page.items)
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
    }
  }, [client, instanceId])

  useEffect(() => {
    void load()
  }, [load])

  function startEdit(contact: Contact) {
    // Поле начинается пустым, а не с текущим именем: правка вводит новое
    // имя целиком, а не редактирует старое по буквам.
    setEditingId(contact.id)
    setDraftName('')
  }

  async function saveEdit(id: string) {
    const trimmed = draftName.trim()
    if (trimmed === '' || busy.has(id)) return

    setBusy((current) => new Set(current).add(id))
    setError(null)
    try {
      await client.renameContact(id, trimmed)
      setEditingId(null)
      await load()
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
    } finally {
      setBusy((current) => {
        const next = new Set(current)
        next.delete(id)
        return next
      })
    }
  }

  async function resetToChannel(id: string) {
    if (busy.has(id)) return

    setBusy((current) => new Set(current).add(id))
    setError(null)
    try {
      // null возвращает управление именем каналу — ручная правка снимается,
      // а не заменяется пустой строкой (пустая строка была бы новым ручным
      // именем, а не отказом от ручного имени).
      await client.renameContact(id, null)
      await load()
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
    } finally {
      setBusy((current) => {
        const next = new Set(current)
        next.delete(id)
        return next
      })
    }
  }

  return (
    <section aria-label="Контакты" className="card">
      {error !== null && <Alert onDismiss={() => setError(null)}>{error}</Alert>}

      {contacts === null ? (
        <p>Загрузка контактов…</p>
      ) : contacts.length === 0 ? (
        <div className="empty-state">
          <IconContacts />
          <p>Контактов пока нет — они появятся сами по мере переписки на этом номере.</p>
        </div>
      ) : (
        <ul className="entity-list">
          {contacts.map((contact) => {
            const rowBusy = busy.has(contact.id)
            const isEditing = editingId === contact.id
            return (
              <li key={contact.id} className="entity-row">
                <p className="entity-row__title">
                  {contact.phone}
                  {contact.name !== null ? ` — ${contact.name}` : ''}
                  {contact.nameSource === 'manual' && <span title="Имя задано вручную"> (вручную)</span>}
                </p>

                {isEditing ? (
                  <form
                    className="form-inline"
                    onSubmit={(event) => {
                      event.preventDefault()
                      void saveEdit(contact.id)
                    }}
                  >
                    <div className="field">
                      <label htmlFor={`contact-name-${contact.id}`}>Имя</label>
                      <input
                        id={`contact-name-${contact.id}`}
                        value={draftName}
                        onChange={(event) => setDraftName(event.target.value)}
                      />
                    </div>
                    <button type="submit" className="btn btn--primary" disabled={rowBusy}>
                      Сохранить
                    </button>
                  </form>
                ) : (
                  canWrite && (
                    <div className="btn-row">
                      <button type="button" className="btn" disabled={rowBusy} onClick={() => startEdit(contact)}>
                        Переименовать
                      </button>
                      {contact.nameSource === 'manual' && (
                        <button
                          type="button"
                          className="btn"
                          disabled={rowBusy}
                          onClick={() => void resetToChannel(contact.id)}
                        >
                          Вернуть имя из канала
                        </button>
                      )}
                    </div>
                  )
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
